import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  Chip,
  Container,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { format } from 'date-fns';
import { getReportTrackingStats } from '../../../services/reportTrackService';
import Loading from '../../../layouts/loading';

const queryClient = new QueryClient();

const formatDateTime = (d) => {
  if (!d) return null;
  try {
    return format(new Date(d), 'dd MMM yyyy, hh:mm a');
  } catch {
    return d;
  }
};

function TrackerContent() {
  const [filters, setFilters] = useState({ weekStartDate: undefined, projectId: undefined });
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20 });

  const { data: statsData, isLoading } = useQuery({
    queryKey: ['weeklyReportStats', filters, pagination],
    queryFn: () =>
      getReportTrackingStats(filters, pagination).then((r) => r.data?.data),
    placeholderData: (prev) => prev,
  });

  const rows = statsData?.rows || [];
  const totalCount = statsData?.count || 0;

  const handleWeekChange = (date) =>
    setFilters((f) => ({
      ...f,
      weekStartDate: date ? date.format('YYYY-MM-DD') : undefined,
    }));

  const handleProjectIdChange = (e) =>
    setFilters((f) => ({
      ...f,
      projectId: e.target.value || undefined,
    }));

  const handlePageChange = (_, newPage) =>
    setPagination((p) => ({ ...p, page: newPage + 1 }));

  const handleRowsPerPageChange = (e) =>
    setPagination({ page: 1, pageSize: parseInt(e.target.value, 10) });

  return (
    <>
      <Helmet>
        <title>Weekly Report Tracker</title>
      </Helmet>
      <Container maxWidth="xl">
        {isLoading && <Loading open={isLoading} />}

        <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
          Weekly Report Tracker
        </Typography>

        {/* Filters */}
        <Card sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Week Start (Monday)"
                value={filters.weekStartDate ? dayjs(filters.weekStartDate) : null}
                onChange={handleWeekChange}
                renderInput={(params) => (
                  <TextField {...params} size="small" sx={{ width: 200 }} />
                )}
              />
            </LocalizationProvider>
            <TextField
              size="small"
              label="Project ID"
              type="number"
              value={filters.projectId || ''}
              onChange={handleProjectIdChange}
              sx={{ width: 140 }}
            />
          </Stack>
        </Card>

        {/* Summary chips */}
        {statsData && (
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Chip label={`${totalCount} total sends`} variant="outlined" size="small" />
            <Chip
              label={`${rows.filter((r) => r.emailOpenedAt).length} opened (this page)`}
              color="success"
              variant="outlined"
              size="small"
            />
            <Chip
              label={`${rows.filter((r) => r.clockiesViewedAt).length} viewed in app (this page)`}
              color="info"
              variant="outlined"
              size="small"
            />
          </Stack>
        )}

        {/* Table */}
        <Card>
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Project</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Recipient</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Week Start</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Sent At</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Email Opened</TableCell>
                  <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Opens</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Viewed in App</TableCell>
                  <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Views</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      {isLoading ? 'Loading…' : 'No data found. Adjust filters or wait for the next report run.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {row.project?.projectName || `#${row.projectId}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {row.recipientUser?.userName || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.recipientEmail}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.weekStartDate}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {formatDateTime(row.sentAt) || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {row.emailOpenedAt ? (
                          <Chip
                            label={formatDateTime(row.emailOpenedAt)}
                            color="success"
                            size="small"
                            sx={{ maxWidth: 200 }}
                          />
                        ) : (
                          <Chip label="Not opened" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{row.emailOpenCount}</Typography>
                      </TableCell>
                      <TableCell>
                        {row.clockiesViewedAt ? (
                          <Chip
                            label={formatDateTime(row.clockiesViewedAt)}
                            color="info"
                            size="small"
                            sx={{ maxWidth: 200 }}
                          />
                        ) : (
                          <Chip label="Not viewed" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{row.clockiesViewCount}</Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <TablePagination
            component="div"
            count={totalCount}
            page={pagination.page - 1}
            rowsPerPage={pagination.pageSize}
            rowsPerPageOptions={[10, 20, 50]}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Card>
      </Container>
    </>
  );
}

export default function WeeklyReportTracker() {
  return (
    <QueryClientProvider client={queryClient}>
      <TrackerContent />
    </QueryClientProvider>
  );
}
